import type { StateId, StatusCode, Tier } from './types';
import type { IconName } from '../components/icons';

/**
 * Definice stavů – převzato 1:1 z prototypu `sklad-ui-prototyp.html`, objekt STATES.
 * Kde se implementace liší od prototypu, vyhrává prototyp.
 * Kde se prototyp liší od specu, vyhrává spec.
 */
export interface StateDef {
  id: StateId;
  tier: Tier;
  icon: IconName;
  accent: string;
  /** Headline se vykreslí akcentní barvou (ZÍTRA NEJEDE). */
  warnHl?: boolean;
  /** Stav nese okno s datem doručení. */
  until?: boolean;
  /** Datum doručení ještě není určeno – místo data se zobrazí „Ještě neurčen“. */
  untilUnknown?: boolean;
  /** Ve verdiktu se nevypisuje název stavu před popisem. */
  noName?: boolean;
}

export const STATES: Record<StateId, StateDef> = {
  preprava: { id: 'preprava', tier: 'go', icon: 'truck', accent: '#046C4E' },
  schvaleni: {
    id: 'schvaleni',
    tier: 'neutral',
    icon: 'calx',
    accent: '#175CD3',
    warnHl: true,
    until: true,
    untilUnknown: true,
  },
  priprava: { id: 'priprava', tier: 'neutral', icon: 'box', accent: '#175CD3', until: true },
  vraceni: { id: 'vraceni', tier: 'neutral', icon: 'uturn', accent: '#175CD3', until: true },
  dokoncena: { id: 'dokoncena', tier: 'stop', icon: 'stopsign', accent: '#fff' },
  zrusena: { id: 'zrusena', tier: 'stop', icon: 'stopsign', accent: '#fff' },
  vraceno: { id: 'vraceno', tier: 'stop', icon: 'stopsign', accent: '#fff' },
  nenalezena: {
    id: 'nenalezena',
    tier: 'notfound',
    icon: 'searchoff',
    accent: '#B42318',
    noName: true,
  },
  neznamy: { id: 'neznamy', tier: 'unknown', icon: 'quest', accent: '#5B6675' },
};

/** Barvy jednotlivých vrstev – prototyp, objekt TIER. */
export interface TierTokens {
  page: string;
  vBg: string;
  vInk: string;
  vIco: string | null;
  solid: boolean;
  bar: string;
  barLine: string;
  barInk: string;
  barMuted: string;
  /** Status bar telefonu na tmavém podkladu. */
  darkStatusBar: boolean;
}

export const TIER: Record<Tier, TierTokens> = {
  go: {
    page: '#DCF2E7', vBg: '#046C4E', vInk: '#fff', vIco: '#fff', solid: true,
    bar: '#EAF8F1', barLine: '#BFE6D2', barInk: '#101828', barMuted: '#3D5A4C', darkStatusBar: false,
  },
  neutral: {
    page: '#F2F4F7', vBg: '#FFFFFF', vInk: '#101828', vIco: null, solid: false,
    bar: '#FFFFFF', barLine: '#E4E7EC', barInk: '#101828', barMuted: '#5B6675', darkStatusBar: false,
  },
  unknown: {
    page: '#F2F4F7', vBg: '#FFFFFF', vInk: '#101828', vIco: null, solid: false,
    bar: '#FFFFFF', barLine: '#E4E7EC', barInk: '#101828', barMuted: '#5B6675', darkStatusBar: false,
  },
  notfound: {
    page: '#F2F4F7', vBg: '#FFFFFF', vInk: '#101828', vIco: null, solid: false,
    bar: '#FFFFFF', barLine: '#E4E7EC', barInk: '#101828', barMuted: '#5B6675', darkStatusBar: false,
  },
  stop: {
    page: '#B42318', vBg: '#912018', vInk: '#fff', vIco: '#fff', solid: true,
    bar: '#912018', barLine: '#7A1B15', barInk: '#FFFFFF', barMuted: '#FFD9D5', darkStatusBar: true,
  },
};

/** Mapování enumu z backendu (cíl, A.3) na interní id stavu. */
const BY_CODE: Record<StatusCode, StateId> = {
  TRANSPORT: 'preprava',
  TRANSPORT_APPROVAL: 'schvaleni',
  PREPARATION: 'priprava',
  RETURNING: 'vraceni',
  COMPLETED: 'dokoncena',
  CANCELLED: 'zrusena',
  RETURNED: 'vraceno',
  // Stav CHYBA (C.1) vzniká po nahlášení dispečerům – patří do červené vrstvy.
  ERROR: 'dokoncena',
};

/**
 * Přechodné mapování z českého textu, který API posílá dnes.
 * Odstranit, jakmile backend dodá `statusCode` (F2.1).
 * Normalizuje diakritiku i velikost písmen, protože texty z různých míst systému
 * se v obojím liší.
 */
const BY_CZECH_LABEL: Record<string, StateId> = {
  preprava: 'preprava',
  'schvaleni prepravy': 'schvaleni',
  'priprava zasilky': 'priprava',
  'vraceni odesilateli': 'vraceni',
  dokoncena: 'dokoncena',
  zrusena: 'zrusena',
  'vraceno odesilateli': 'vraceno',
};

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Vrátí definici stavu. Neznámý nebo prázdný stav padá na EDGE („Neznámý stav“),
 * NIKDY ne na GO – to je bezpečnostní pojistka, ne kosmetika (spec 10).
 */
export function resolveState(
  statusCode: StatusCode | null,
  rawLabel: string | null,
): StateDef {
  if (statusCode && BY_CODE[statusCode]) return STATES[BY_CODE[statusCode]];
  if (rawLabel) {
    const id = BY_CZECH_LABEL[normalize(rawLabel)];
    if (id) return STATES[id];
  }
  return STATES.neznamy;
}
